import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react'

import { useCopilotAction, useCopilotContext } from '@copilotkit/react-core'
import { F0AiProposalCard, F0Box, F0Text, modules } from '@factorialco/f0-react'
import type { ModuleId } from '@factorialco/f0-react'

import { useAiChat } from 'modules/ai/chat/useAiChat'

import i18n from 'modules/core/lib/i18n'
import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import { useOneActionNames } from 'modules/core/lib/oneCreateActions'

type EntityProposalStatus = 'idle' | 'pending' | 'confirmed'

interface ProposeEntityCreationArgs {
  proposalId: string
  entityName: string
  title: string
  description: string
  subtitle?: string
  module?: ModuleId
}

interface EntityProposalState {
  latestProposalId: string | null
  latestProposalStatus: EntityProposalStatus
}

const DEFAULT_PROPOSAL_SCOPE = 'default'
const MAX_ENTITY_PROPOSAL_SCOPES = 25
const MAX_ENTITY_PROPOSAL_STATUSES = 100
const EMPTY_ENTITY_PROPOSAL_STATE: EntityProposalState = {
  latestProposalId: null,
  latestProposalStatus: 'idle',
}
const F0_MODULE_IDS = Object.keys(modules)
const F0_MODULE_ID_SET = new Set(F0_MODULE_IDS)

const EntityProposalErrorFallback = () => (
  <F0Box role="alert">
    <F0Text content={i18n.t('common.ai.proposal.render_error')} />
  </F0Box>
)

const EntityProposalErrorBoundary = ({ children }: { children: ReactNode }) => (
  <ErrorBoundary customCrashComponent={<EntityProposalErrorFallback />}>{children}</ErrorBoundary>
)

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const isModuleId = (value: unknown): value is ModuleId =>
  typeof value === 'string' && F0_MODULE_ID_SET.has(value)

const normalizeString = (value: unknown) => {
  if (typeof value !== 'string') return undefined

  const trimmedValue = value.trim()
  return trimmedValue || undefined
}

const parseProposeEntityCreationArgs = (value: unknown): ProposeEntityCreationArgs | null => {
  if (!isRecord(value)) return null

  const { proposalId, entityName, title, description, subtitle, module } = value
  const normalizedProposalId = normalizeString(proposalId)
  const normalizedEntityName = normalizeString(entityName)
  const normalizedTitle = normalizeString(title)
  const normalizedDescription = normalizeString(description)

  if (
    !normalizedProposalId ||
    !normalizedEntityName ||
    !normalizedTitle ||
    !normalizedDescription
  ) {
    return null
  }

  return {
    proposalId: normalizedProposalId,
    entityName: normalizedEntityName,
    title: normalizedTitle,
    description: normalizedDescription,
    subtitle: normalizeString(subtitle),
    module: isModuleId(module) ? module : undefined,
  }
}

const parseProposalId = (value: unknown) => {
  if (!isRecord(value)) return undefined

  const { proposalId } = value
  return normalizeString(proposalId)
}

// Tool-call renderers and handlers mount independently, so completion events
// need a shared store to update proposal cards rendered by previous messages.
const entityProposalStore = (() => {
  let states = new Map<string, EntityProposalState>()
  let proposalStatuses = new Map<string, EntityProposalStatus>()
  const listeners = new Set<() => void>()

  const emit = () => listeners.forEach((listener) => listener())
  const getState = (scopeId: string) => states.get(scopeId) ?? EMPTY_ENTITY_PROPOSAL_STATE
  const setProposalStatusValue = (proposalId: string, status: EntityProposalStatus) => {
    const nextProposalStatuses = new Map(proposalStatuses)
    nextProposalStatuses.delete(proposalId)
    nextProposalStatuses.set(proposalId, status)

    while (nextProposalStatuses.size > MAX_ENTITY_PROPOSAL_STATUSES) {
      const oldestProposalId = nextProposalStatuses.keys().next().value
      if (!oldestProposalId) break
      nextProposalStatuses.delete(oldestProposalId)
    }

    proposalStatuses = nextProposalStatuses
  }
  const setState = (scopeId: string, state: EntityProposalState) => {
    const nextStates = new Map(states)
    nextStates.delete(scopeId)
    nextStates.set(scopeId, state)

    while (nextStates.size > MAX_ENTITY_PROPOSAL_SCOPES) {
      const oldestScopeId = nextStates.keys().next().value
      if (!oldestScopeId) break
      nextStates.delete(oldestScopeId)
    }

    states = nextStates
  }

  return {
    getSnapshot: (scopeId: string) => getState(scopeId),
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    registerProposal: (scopeId: string, args: ProposeEntityCreationArgs) => {
      const currentState = getState(scopeId)
      const persistedStatus = proposalStatuses.get(args.proposalId)
      const latestProposalStatus =
        persistedStatus ??
        (currentState.latestProposalId === args.proposalId
          ? currentState.latestProposalStatus
          : 'idle')
      setProposalStatusValue(args.proposalId, latestProposalStatus)

      setState(scopeId, {
        latestProposalId: args.proposalId,
        latestProposalStatus,
      })
      emit()
    },
    setLatestProposalStatus: (scopeId: string, status: Exclude<EntityProposalStatus, 'idle'>) => {
      const currentState = getState(scopeId)
      if (!currentState.latestProposalId) return

      setProposalStatusValue(currentState.latestProposalId, status)
      setState(scopeId, { ...currentState, latestProposalStatus: status })
      emit()
    },
    setProposalStatus: (proposalId: string, status: Exclude<EntityProposalStatus, 'idle'>) => {
      setProposalStatusValue(proposalId, status)
      const matchingScopeIds = new Set<string>()
      states.forEach((state, stateScopeId) => {
        if (state.latestProposalId === proposalId) matchingScopeIds.add(stateScopeId)
      })

      if (matchingScopeIds.size === 0) return

      matchingScopeIds.forEach((matchingScopeId) => {
        setState(matchingScopeId, {
          latestProposalId: proposalId,
          latestProposalStatus: status,
        })
      })
      emit()
    },
  }
})()

const getProposalScopeId = (threadId?: string | null) => threadId || DEFAULT_PROPOSAL_SCOPE

const useEntityProposalState = (scopeId: string) =>
  useSyncExternalStore(
    entityProposalStore.subscribe,
    () => entityProposalStore.getSnapshot(scopeId),
    () => entityProposalStore.getSnapshot(scopeId)
  )

const EntityProposalCard = ({
  proposal,
  scopeId,
}: {
  proposal: ProposeEntityCreationArgs
  scopeId: string
}) => {
  const aiChat = useAiChat()
  const { latestProposalId, latestProposalStatus } = useEntityProposalState(scopeId)
  const showConfirmButton =
    proposal.proposalId === latestProposalId && latestProposalStatus === 'idle'

  const handleConfirm = () => {
    entityProposalStore.setLatestProposalStatus(scopeId, 'pending')
    aiChat.sendMessage(i18n.t('common.ai.proposal.confirm_message'))
  }

  const proposalCardProps = {
    module: proposal.module,
    heading: i18n.t('common.ai.proposal.default_heading'),
    title: proposal.title,
    subtitle: proposal.subtitle,
    description: proposal.description,
    seeMoreLabel: i18n.t('common.see_more'),
  }

  if (!showConfirmButton) {
    return <F0AiProposalCard {...proposalCardProps} showActions={false} />
  }

  return (
    <F0AiProposalCard
      {...proposalCardProps}
      primaryActionLabel={i18n.t('common.confirm')}
      onPrimaryAction={handleConfirm}
    />
  )
}

const EntityProposalTurn = ({
  args,
  scopeId,
  shouldRegisterAsLatest,
}: {
  args: ProposeEntityCreationArgs
  scopeId: string
  shouldRegisterAsLatest: boolean
}) => {
  const [proposal] = useState(() => args)

  useEffect(() => {
    if (shouldRegisterAsLatest) {
      entityProposalStore.registerProposal(scopeId, proposal)
    }
  }, [proposal, scopeId, shouldRegisterAsLatest])

  return <EntityProposalCard proposal={proposal} scopeId={scopeId} />
}

const EntityProposalCompletion = ({
  proposalId,
  scopeId,
}: {
  proposalId?: string
  scopeId: string
}) => {
  useEffect(() => {
    markEntityProposalCompleted(scopeId, proposalId)
  }, [proposalId, scopeId])

  return <></>
}

const markEntityProposalCompleted = (scopeId: string, proposalId?: string) => {
  if (proposalId) {
    entityProposalStore.setProposalStatus(proposalId, 'confirmed')
  } else {
    entityProposalStore.setLatestProposalStatus(scopeId, 'confirmed')
  }
}

export const useProposeEntityCreationAction = () => {
  const { threadId } = useCopilotContext()
  const proposalScopeId = getProposalScopeId(threadId)
  const availableActions = useOneActionNames()
  const proposeEntityCreationAvailable = availableActions.length > 0 ? 'frontend' : 'disabled'

  useCopilotAction({
    name: 'proposeEntityCreation',
    description:
      'Show a generic entity creation proposal card before creating an entity. The card uses generic frontend copy; do not include hidden identifiers or internal fields in title, subtitle, or description.',
    followUp: false,
    available: proposeEntityCreationAvailable,
    parameters: [
      {
        name: 'proposalId',
        description: 'Internal proposal identifier. It is not shown to the user.',
        required: true,
      },
      {
        name: 'entityName',
        description:
          'The registered create entity action name. This must match the entityName that will later be passed to createEntity.',
        required: true,
        enum: availableActions,
      },
      {
        name: 'title',
        description:
          'The proposed entity title to display. Keep it concise and user-facing; do not include hidden identifiers, internal fields, or implementation details.',
        required: true,
      },
      {
        name: 'subtitle',
        description:
          'Optional user-facing metadata line for the proposal card, such as a team and category. Do not include hidden IDs or internal fields.',
        required: false,
      },
      {
        name: 'module',
        description: 'Optional F0 module identifier used only to show the proposal card icon.',
        required: false,
        enum: F0_MODULE_IDS,
      },
      {
        name: 'description',
        description: 'The proposed entity description to display.',
        required: true,
      },
      {
        name: 'metadata',
        description:
          'Optional hidden proposal payload for agent context. This is never displayed and must be passed explicitly to createEntity later if needed.',
        required: false,
      },
    ],
    render: ({ status, args }) => {
      const parsedArgs = parseProposeEntityCreationArgs(args)
      if (!parsedArgs) {
        if (status === 'inProgress') return <></>

        return (
          <F0Box role="alert">
            <F0Text content={i18n.t('common.ai.proposal.incomplete_error')} />
          </F0Box>
        )
      }
      if (!availableActions.includes(parsedArgs.entityName)) {
        return (
          <F0Box role="alert">
            <F0Text content={i18n.t('common.ai.proposal.unknown_entity_error')} />
          </F0Box>
        )
      }

      return (
        <EntityProposalErrorBoundary>
          <EntityProposalTurn
            args={parsedArgs}
            scopeId={proposalScopeId}
            shouldRegisterAsLatest={status !== 'complete'}
          />
        </EntityProposalErrorBoundary>
      )
    },
  })

  useCopilotAction({
    name: 'markEntityProposalCompleted',
    description: 'Mark an entity proposal card as completed and remove its confirmation button.',
    followUp: false,
    available: 'frontend',
    parameters: [
      {
        name: 'proposalId',
        description: 'The proposal identifier from proposeEntityCreation.',
        required: false,
      },
    ],
    render: ({ status, args }) => {
      const proposalId = parseProposalId(args)
      if (status === 'inProgress' && !proposalId) return <></>

      return <EntityProposalCompletion proposalId={proposalId} scopeId={proposalScopeId} />
    },
  })
}
